import { useCallback, useState } from 'react';
import {
  Banner, Card, ConfirmButton, Empty, Field, Modal, Tabs, UnitBadge,
} from '../components/ui';
import { useFlash, useNow, useRealtime, useResource } from '../state/hooks';
import { api } from '../lib/api';
import { clock, dateTime, elapsed, titleCase } from '../lib/format';
import type {
  AuditEntry, BusinessHours, CallQueue, Category, DirectoryUser, FieldMessage, IvrMenu,
  ManagedUser, PoliceStation, Priority, RoutingRule, Shift, SipExtension, SipTrunk, Unit,
} from '../lib/types';
import { useAuth } from '../state/AuthContext';
import { Reports } from './Reports';

/**
 * Administration.
 *
 * Grouped by the question being answered rather than by database table: who
 * works here, what rolls, how a call finds a person, what the words mean, and
 * what happened. Secrets are write-only throughout — the API never returns a
 * SIP password, so the forms show whether one is set and nothing more.
 */

type TabId = 'users' | 'units' | 'stations' | 'telephony' | 'taxonomy' | 'messages' | 'reports' | 'system' | 'audit';

export function AdminConsole() {
  const { can } = useAuth();
  const [tab, setTab] = useState<TabId>(can('users.manage') ? 'users' : 'telephony');
  const [flash, showFlash] = useFlash();

  const notify = useCallback(
    (kind: 'ok' | 'error', message: string) => showFlash(kind, message),
    [showFlash],
  );

  const tabs = [
    { id: 'users' as const, label: 'Staff', show: can('users.manage') },
    { id: 'units' as const, label: 'Units', show: can('units.manage') },
    { id: 'stations' as const, label: 'Stations', show: can('units.manage') },
    { id: 'telephony' as const, label: 'Telephony', show: can('system.config') },
    { id: 'taxonomy' as const, label: 'Incident types', show: can('taxonomy.manage') },
    { id: 'messages' as const, label: 'Field messages', show: can('messages.read') },
    { id: 'reports' as const, label: 'Reports', show: can('reports.own') },
    { id: 'system' as const, label: 'System', show: can('system.config') },
    { id: 'audit' as const, label: 'Audit', show: can('audit.read') },
  ].filter((entry) => entry.show);

  return (
    <div className="stack">
      {flash && <Banner kind={flash.kind}>{flash.message}</Banner>}
      <Tabs tabs={tabs} active={tab} onChange={setTab} />

      {tab === 'users' && <StaffPanel notify={notify} />}
      {tab === 'units' && <UnitsPanel notify={notify} />}
      {tab === 'telephony' && <TelephonyPanel notify={notify} />}
      {tab === 'taxonomy' && <TaxonomyPanel notify={notify} />}
      {tab === 'stations' && <StationsPanel notify={notify} />}
      {tab === 'messages' && <MessagesPanel notify={notify} />}
      {/* The same reporting the dedicated page shows — administrators live in
          this console, so the graphs are here too rather than one nav away. */}
      {tab === 'reports' && <Reports />}
      {tab === 'system' && <SystemPanel notify={notify} />}
      {tab === 'audit' && <AuditPanel />}
    </div>
  );
}

type Notify = (kind: 'ok' | 'error', message: string) => void;

async function run(notify: Notify, label: string, action: () => Promise<unknown>, after?: () => void) {
  try {
    await action();
    notify('ok', label);
    after?.();
  } catch (err) {
    notify('error', (err as Error).message);
  }
}

// ---------------------------------------------------------------- staff

function StaffPanel({ notify }: { notify: Notify }) {
  const users = useResource<{ users: ManagedUser[] }>('/users?includeInactive=true');
  const [editing, setEditing] = useState<ManagedUser | 'new' | null>(null);
  const [issued, setIssued] = useState<{ email: string; password: string } | null>(null);

  return (
    <>
      {issued && (
        <Banner kind="warn">
          Temporary password for <strong>{issued.email}</strong>:{' '}
          <code className="mono">{issued.password}</code> — shown once. The account must change it at first
          sign-in.
        </Banner>
      )}

      <Card
        title={`Staff (${users.data?.users.length ?? 0})`}
        flush
        actions={<button className="btn primary sm" onClick={() => setEditing('new')}>Add person</button>}
      >
        {!users.data ? (
          <Empty>Loading…</Empty>
        ) : (
          <div className="scroll-md">
            <table>
              <thead>
                <tr>
                  <th>Name</th><th>Role</th><th>Phone position</th><th>Extension</th>
                  <th>Last sign-in</th><th></th>
                </tr>
              </thead>
              <tbody>
                {users.data.users.map((user) => (
                  <tr key={user.id} style={user.deactivated_at ? { opacity: 0.5 } : undefined}>
                    <td>
                      <strong>{user.full_name}</strong>
                      <div className="faint">{user.email}</div>
                    </td>
                    <td>{titleCase(user.role)}</td>
                    <td>{user.can_take_calls || user.role === 'call_taker' ? 'Yes' : '—'}</td>
                    <td className="mono">{user.sip_extension ?? '—'}</td>
                    <td className="faint">{user.last_login_at ? dateTime(user.last_login_at) : 'never'}</td>
                    <td>
                      <div className="row tight">
                        <button className="btn sm" onClick={() => setEditing(user)}>Edit</button>
                        <button
                          className="btn sm"
                          onClick={() =>
                            void run(notify, 'Password reset', async () => {
                              const result = await api.post<{ temporaryPassword: string }>(
                                `/users/${user.id}/reset-password`,
                              );
                              setIssued({ email: user.email, password: result.temporaryPassword });
                            })
                          }
                        >
                          Reset password
                        </button>
                        {user.deactivated_at ? (
                          <button
                            className="btn sm"
                            onClick={() =>
                              void run(notify, 'Account reactivated',
                                () => api.post(`/users/${user.id}/reactivate`), users.reload)
                            }
                          >
                            Reactivate
                          </button>
                        ) : (
                          <ConfirmButton
                            label="Deactivate"
                            onConfirm={() =>
                              void run(notify, 'Account deactivated',
                                () => api.post(`/users/${user.id}/deactivate`), users.reload)
                            }
                          />
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {editing && (
        <StaffDialog
          user={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(credentials) => {
            setEditing(null);
            if (credentials) setIssued(credentials);
            users.reload();
            notify('ok', 'Saved.');
          }}
          onError={(message) => notify('error', message)}
        />
      )}
    </>
  );
}

function StaffDialog({
  user,
  onClose,
  onSaved,
  onError,
}: {
  user: ManagedUser | null;
  onClose: () => void;
  onSaved: (credentials: { email: string; password: string } | null) => void;
  onError: (message: string) => void;
}) {
  const [form, setForm] = useState({
    email: user?.email ?? '',
    fullName: user?.full_name ?? '',
    role: user?.role ?? 'call_taker',
    canTakeCalls: user?.can_take_calls ?? false,
    sipExtension: user?.sip_extension ?? '',
    badgeNo: user?.badge_no ?? '',
    phone: user?.phone ?? '',
  });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const body = {
        fullName: form.fullName,
        role: form.role,
        canTakeCalls: form.canTakeCalls,
        sipExtension: form.sipExtension || null,
        badgeNo: form.badgeNo || null,
        phone: form.phone || null,
      };
      if (user) {
        await api.patch(`/users/${user.id}`, { ...body, email: form.email });
        onSaved(null);
      } else {
        const created = await api.post<{ temporaryPassword: string }>('/users', {
          ...body,
          email: form.email,
        });
        onSaved({ email: form.email, password: created.temporaryPassword });
      }
    } catch (err) {
      onError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={user ? `Edit ${user.full_name}` : 'Add a person'}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn primary" disabled={busy || !form.email || !form.fullName} onClick={() => void submit()}>
            Save
          </button>
        </>
      }
    >
      <div className="stack">
        <Field label="Full name">
          {(id) => (
            <input id={id} value={form.fullName} onChange={(e) => setForm({ ...form, fullName: e.target.value })} />
          )}
        </Field>
        <Field label="Email">
          {(id) => (
            <input id={id} type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          )}
        </Field>
        <Field label="Role" hint="Changing a role signs the account out so it re-authenticates with new permissions.">
          {(id) => (
            <select id={id} value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value as ManagedUser['role'] })}>
              <option value="call_taker">Call taker</option>
              <option value="dispatcher">Dispatcher</option>
              <option value="admin">Administrator</option>
            </select>
          )}
        </Field>
        <label className="checkbox">
          <input
            type="checkbox"
            checked={form.canTakeCalls || form.role === 'call_taker'}
            disabled={form.role === 'call_taker'}
            onChange={(e) => setForm({ ...form, canTakeCalls: e.target.checked })}
          />
          Staffs a phone position (gets a softphone)
        </label>
        <div className="columns three">
          <Field label="SIP extension">
            {(id) => (
              <input id={id} value={form.sipExtension} onChange={(e) => setForm({ ...form, sipExtension: e.target.value })} />
            )}
          </Field>
          <Field label="Badge number">
            {(id) => <input id={id} value={form.badgeNo} onChange={(e) => setForm({ ...form, badgeNo: e.target.value })} />}
          </Field>
          <Field label="Contact number">
            {(id) => <input id={id} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />}
          </Field>
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------- units

function UnitsPanel({ notify }: { notify: Notify }) {
  const units = useResource<{ units: Unit[] }>('/units?includeInactive=true');
  const directory = useResource<{ users: DirectoryUser[] }>('/users/directory');
  const now = useNow(10_000);
  const [form, setForm] = useState({ callSign: '', name: '', kind: 'patrol', operatorId: '' });

  return (
    <div className="columns wide-side">
      <Card title={`Units (${units.data?.units.length ?? 0})`} flush>
        {!units.data ? (
          <Empty>Loading…</Empty>
        ) : (
          <div className="scroll-md">
            <table>
              <thead>
                <tr><th>Call sign</th><th>Type</th><th>Status</th><th>Crew</th><th>Position</th><th></th></tr>
              </thead>
              <tbody>
                {units.data.units.map((unit) => (
                  <tr key={unit.id} style={unit.deactivated_at ? { opacity: 0.5 } : undefined}>
                    <td>
                      <strong>{unit.call_sign}</strong>
                      <div className="faint">{unit.name}</div>
                    </td>
                    <td>{titleCase(unit.kind)}</td>
                    <td><UnitBadge status={unit.status} /></td>
                    <td>
                      <select
                        value={unit.operator_id ?? ''}
                        onChange={(event) =>
                          void run(notify, 'Crew updated', () =>
                            api.patch(`/units/${unit.id}`, { operatorId: event.target.value || null }),
                            units.reload,
                          )
                        }
                      >
                        <option value="">Unassigned</option>
                        {(directory.data?.users ?? []).map((user) => (
                          <option key={user.id} value={user.id}>{user.full_name}</option>
                        ))}
                      </select>
                    </td>
                    <td className="faint">
                      {unit.latitude == null
                        ? 'never reported'
                        : `${elapsed(unit.location_updated_at, now)} ago`}
                    </td>
                    <td>
                      {!unit.deactivated_at && (
                        <ConfirmButton
                          label="Retire"
                          confirmLabel="Retire unit?"
                          onConfirm={() =>
                            void run(notify, 'Unit retired', () => api.del(`/units/${unit.id}`), units.reload)
                          }
                        />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card title="Add a unit">
        <form
          className="stack"
          onSubmit={(event) => {
            event.preventDefault();
            void run(notify, `${form.callSign} added`, async () => {
              await api.post('/units', {
                callSign: form.callSign,
                name: form.name,
                kind: form.kind,
                operatorId: form.operatorId || null,
              });
              setForm({ callSign: '', name: '', kind: 'patrol', operatorId: '' });
            }, units.reload);
          }}
        >
          <Field label="Call sign" hint="Short, unique, and what the radio actually says.">
            {(id) => (
              <input id={id} required value={form.callSign} onChange={(e) => setForm({ ...form, callSign: e.target.value })} />
            )}
          </Field>
          <Field label="Description">
            {(id) => <input id={id} required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />}
          </Field>
          <Field label="Type">
            {(id) => (
              <select id={id} value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}>
                {['patrol', 'ambulance', 'fire', 'supervisor', 'utility', 'other'].map((kind) => (
                  <option key={kind} value={kind}>{titleCase(kind)}</option>
                ))}
              </select>
            )}
          </Field>
          <button className="btn primary" type="submit" disabled={!form.callSign || !form.name}>Add unit</button>
        </form>
      </Card>
    </div>
  );
}


// ---------------------------------------------------------------- stations

/**
 * The police stations incidents are dispatched to.
 *
 * The seeded list ships with approximate coordinates, and says so: the dispatch
 * console orders stations by distance, so a wrong position produces a wrong
 * suggestion. Correcting one here is what marks it confirmed.
 */
function StationsPanel({ notify }: { notify: Notify }) {
  const stations = useResource<{ stations: PoliceStation[] }>('/stations?includeInactive=true');
  const [editing, setEditing] = useState<PoliceStation | null>(null);
  const [form, setForm] = useState({ code: '', name: '', zone: '', address: '', phone: '' });

  const unverified = (stations.data?.stations ?? []).filter((s) => !s.location_verified && !s.deactivated_at);

  return (
    <div className="stack">
      {unverified.length > 0 && (
        <Banner kind="warn">
          {unverified.length} station{unverified.length === 1 ? ' has an' : 's have'} approximate
          coordinates that nobody has confirmed. Dispatch suggests the nearest station by distance,
          so these should be checked against the authoritative list before anyone relies on them.
        </Banner>
      )}

      <div className="columns wide-side">
        <Card title={`Police stations (${stations.data?.stations.length ?? 0})`} flush>
          {!stations.data ? <Empty>Loading…</Empty> : (
            <div className="scroll" style={{ maxHeight: 'calc(100vh - 320px)' }}>
              <table>
                <thead>
                  <tr>
                    <th>Station</th><th>Zone</th><th>Contact</th>
                    <th>Position</th><th className="num">Open</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {stations.data.stations.map((station) => (
                    <tr key={station.id} style={station.deactivated_at ? { opacity: 0.5 } : undefined}>
                      <td>
                        <div className="row tight">
                          <span className="badge neutral mono">{station.code}</span>
                          <strong>{station.name}</strong>
                        </div>
                        {station.address && <div className="faint">{station.address}</div>}
                      </td>
                      <td>{station.zone ?? <span className="faint">—</span>}</td>
                      <td className="faint">{station.phone ?? '—'}</td>
                      <td>
                        {station.latitude == null ? (
                          <span className="faint">not set</span>
                        ) : station.location_verified ? (
                          <span className="badge ok">confirmed</span>
                        ) : (
                          <span className="badge warn" title="Seeded position, never checked">approximate</span>
                        )}
                      </td>
                      <td className="num">{station.open_incidents || <span className="faint">—</span>}</td>
                      <td>
                        <div className="row tight">
                          <button className="btn sm" onClick={() => setEditing(station)}>Edit</button>
                          {!station.deactivated_at && (
                            <ConfirmButton
                              label="Retire"
                              confirmLabel="Retire station?"
                              onConfirm={() =>
                                void run(notify, `${station.name} retired`,
                                  () => api.del(`/stations/${station.id}`), stations.reload)
                              }
                            />
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <Card title="Add a station">
          <form
            className="stack"
            onSubmit={(event) => {
              event.preventDefault();
              void run(notify, `${form.name} saved`, async () => {
                await api.post('/stations', {
                  code: form.code,
                  name: form.name,
                  zone: form.zone || null,
                  address: form.address || null,
                  phone: form.phone || null,
                });
                setForm({ code: '', name: '', zone: '', address: '', phone: '' });
              }, stations.reload);
            }}
          >
            <Field label="Code" hint="Short and unique, e.g. ABP.">
              {(id) => (
                <input id={id} required value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} />
              )}
            </Field>
            <Field label="Name">
              {(id) => <input id={id} required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />}
            </Field>
            <Field label="Zone">
              {(id) => <input id={id} value={form.zone} onChange={(e) => setForm({ ...form, zone: e.target.value })} />}
            </Field>
            <Field label="Address">
              {(id) => <input id={id} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />}
            </Field>
            <Field label="Phone">
              {(id) => <input id={id} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />}
            </Field>
            <button className="btn primary" type="submit" disabled={!form.code || !form.name}>Add station</button>
          </form>
        </Card>
      </div>

      {editing && (
        <StationDialog
          station={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); stations.reload(); notify('ok', 'Station updated.'); }}
          onError={(message) => notify('error', message)}
        />
      )}
    </div>
  );
}

function StationDialog({
  station,
  onClose,
  onSaved,
  onError,
}: {
  station: PoliceStation;
  onClose: () => void;
  onSaved: () => void;
  onError: (message: string) => void;
}) {
  const [form, setForm] = useState({
    name: station.name,
    zone: station.zone ?? '',
    address: station.address ?? '',
    phone: station.phone ?? '',
    latitude: station.latitude?.toString() ?? '',
    longitude: station.longitude?.toString() ?? '',
  });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.patch(`/stations/${station.id}`, {
        name: form.name,
        zone: form.zone || null,
        address: form.address || null,
        phone: form.phone || null,
        latitude: form.latitude === '' ? null : Number(form.latitude),
        longitude: form.longitude === '' ? null : Number(form.longitude),
      });
      onSaved();
    } catch (err) {
      onError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={`${station.code} — ${station.name}`}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn primary" disabled={busy || !form.name} onClick={() => void submit()}>Save</button>
        </>
      }
    >
      <div className="stack">
        {!station.location_verified && station.latitude != null && (
          <Banner kind="warn">
            This position was seeded and never checked. Saving a coordinate here marks it confirmed.
          </Banner>
        )}
        <Field label="Name">
          {(id) => <input id={id} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />}
        </Field>
        <div className="columns two">
          <Field label="Zone">
            {(id) => <input id={id} value={form.zone} onChange={(e) => setForm({ ...form, zone: e.target.value })} />}
          </Field>
          <Field label="Phone">
            {(id) => <input id={id} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />}
          </Field>
        </div>
        <Field label="Address">
          {(id) => <input id={id} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />}
        </Field>
        <div className="columns two">
          <Field label="Latitude">
            {(id) => (
              <input id={id} type="number" step="0.000001" value={form.latitude}
                onChange={(e) => setForm({ ...form, latitude: e.target.value })} />
            )}
          </Field>
          <Field label="Longitude">
            {(id) => (
              <input id={id} type="number" step="0.000001" value={form.longitude}
                onChange={(e) => setForm({ ...form, longitude: e.target.value })} />
            )}
          </Field>
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------- telephony

function TelephonyPanel({ notify }: { notify: Notify }) {
  const trunks = useResource<{ trunks: SipTrunk[] }>('/admin/sip/trunks');
  const extensions = useResource<{ extensions: SipExtension[] }>('/admin/sip/extensions');
  const routing = useResource<{
    rules: RoutingRule[]; menus: IvrMenu[]; hours: BusinessHours[]; queues: CallQueue[];
  }>('/admin/sip/routing');
  const health = useResource<{
    trunks: { id: string; name: string; enabled: boolean }[];
    agents: { id: string; full_name: string; sip_extension: string | null; sip_registered: boolean; phone_state: string; updated_at: string }[];
    mockTelephony: boolean;
    environment: string;
  }>('/telephony/health');
  const [editing, setEditing] = useState<SipTrunk | 'new' | null>(null);

  useRealtime(['system'], () => {
    trunks.reload();
    health.reload();
  });

  return (
    <div className="stack">
      {health.data?.mockTelephony && (
        <Banner kind="warn">
          This instance is running the call simulator. Trunk settings below are stored but not dialled.
        </Banner>
      )}

      <Card
        title="SIP trunks"
        flush
        actions={<button className="btn primary sm" onClick={() => setEditing('new')}>Add trunk</button>}
      >
        {!trunks.data ? <Empty>Loading…</Empty> : trunks.data.trunks.length === 0 ? (
          <Empty>No trunks configured. Add one before agents can register.</Empty>
        ) : (
          <table>
            <thead>
              <tr><th>Name</th><th>WebSocket URI</th><th>Domain</th><th>Auth</th><th>Priority</th><th></th></tr>
            </thead>
            <tbody>
              {trunks.data.trunks.map((trunk) => (
                <tr key={trunk.id}>
                  <td>
                    <strong>{trunk.name}</strong>
                    <div className="row tight">
                      <span className={`badge ${trunk.enabled ? 'ok' : 'neutral'}`}>
                        {trunk.enabled ? 'Enabled' : 'Disabled'}
                      </span>
                      <span className="badge neutral">{trunk.driver}</span>
                    </div>
                  </td>
                  <td className="mono truncate" style={{ maxWidth: 240 }}>{trunk.ws_uri ?? '—'}</td>
                  <td className="mono">{trunk.sip_domain ?? '—'}</td>
                  <td>
                    {trunk.auth_user ?? '—'}
                    <div className="faint">{trunk.has_password ? 'password set' : 'no password'}</div>
                  </td>
                  <td className="num">{trunk.priority}</td>
                  <td>
                    <div className="row tight">
                      <button className="btn sm" onClick={() => setEditing(trunk)}>Edit</button>
                      {trunk.enabled && (
                        <ConfirmButton
                          label="Disable"
                          onConfirm={() =>
                            void run(notify, 'Trunk disabled', () => api.del(`/admin/sip/trunks/${trunk.id}`), trunks.reload)
                          }
                        />
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <div className="columns two">
        <Card title="Handset registration" flush>
          {!health.data ? <Empty>Loading…</Empty> : (
            <table>
              <thead><tr><th>Agent</th><th>Extension</th><th>State</th><th>Seen</th></tr></thead>
              <tbody>
                {health.data.agents.map((agent) => (
                  <tr key={agent.id}>
                    <td>{agent.full_name}</td>
                    <td className="mono">{agent.sip_extension ?? '—'}</td>
                    <td>
                      <span className={`badge ${agent.sip_registered ? 'ok' : 'danger'}`}>
                        <i className="dot" />
                        {agent.sip_registered ? 'Registered' : 'Not registered'}
                      </span>
                      <div className="faint">{titleCase(agent.phone_state)}</div>
                    </td>
                    <td className="faint">{agent.updated_at ? clock(agent.updated_at) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        <Card title="Extensions" flush>
          {!extensions.data ? <Empty>Loading…</Empty> : (
            <div className="scroll-sm">
              <table>
                <thead><tr><th>Ext</th><th>Assigned to</th><th>Trunk</th><th>Secret</th></tr></thead>
                <tbody>
                  {extensions.data.extensions.map((extension) => (
                    <tr key={extension.id}>
                      <td className="mono">{extension.extension}</td>
                      <td>{extension.user_name ?? <span className="faint">unassigned</span>}</td>
                      <td className="faint">{extension.trunk_name ?? '—'}</td>
                      <td className="faint">{extension.has_password ? 'set' : 'none'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      <div className="columns two">
        <Card title="Inbound routing" flush>
          {!routing.data ? <Empty>Loading…</Empty> : (
            <table>
              <thead><tr><th>Rule</th><th>Matches</th><th>Action</th><th></th></tr></thead>
              <tbody>
                {routing.data.rules.map((rule) => (
                  <tr key={rule.id} style={rule.enabled ? undefined : { opacity: 0.5 }}>
                    <td>{rule.name}<div className="faint">order {rule.sort_order}</div></td>
                    <td className="mono truncate" style={{ maxWidth: 160 }}>{rule.match_did ?? 'any number'}</td>
                    <td>{titleCase(rule.action)}<div className="faint">{rule.action_target ?? ''}</div></td>
                    <td>
                      {rule.enabled && (
                        <ConfirmButton
                          label="Disable"
                          onConfirm={() =>
                            void run(notify, 'Rule disabled', () => api.del(`/admin/sip/routing/${rule.id}`), routing.reload)
                          }
                        />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        <Card title="Queues" flush>
          {!routing.data ? <Empty>Loading…</Empty> : (
            <table>
              <thead><tr><th>Queue</th><th>Overflow after</th><th>Then</th><th>Max waiting</th></tr></thead>
              <tbody>
                {routing.data.queues.map((queue) => (
                  <tr key={queue.id}>
                    <td><strong>{queue.name}</strong><div className="faint mono">{queue.code}</div></td>
                    <td>{queue.overflow_after_seconds}s</td>
                    <td>{titleCase(queue.overflow_action)}</td>
                    <td className="num">{queue.max_waiting}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>

      {routing.data && routing.data.menus.length > 0 && (
        <Card title="IVR menus">
          {routing.data.menus.map((menu) => (
            <div key={menu.id} className="stack tight" style={{ marginBottom: 12 }}>
              <strong>{menu.name}</strong>
              <p className="muted" style={{ margin: 0 }}>{menu.prompt}</p>
              <ul className="list">
                {menu.options.map((option) => (
                  <li key={option.digit}>
                    <div className="item" style={{ cursor: 'default' }}>
                      <span className="badge accent mono">{option.digit}</span>
                      <span className="grow">
                        <span className="item-title">{option.label}</span>
                        <span className="item-sub">
                          {titleCase(option.action)} {option.target ? `→ ${option.target}` : ''}
                        </span>
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
              <span className="faint">
                No selection after {menu.timeout_seconds}s → {menu.timeout_action} {menu.timeout_target ?? ''}
              </span>
            </div>
          ))}
        </Card>
      )}

      {editing && (
        <TrunkDialog
          trunk={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            trunks.reload();
            notify('ok', 'Trunk saved.');
          }}
          onError={(message) => notify('error', message)}
        />
      )}
    </div>
  );
}

function TrunkDialog({
  trunk,
  onClose,
  onSaved,
  onError,
}: {
  trunk: SipTrunk | null;
  onClose: () => void;
  onSaved: () => void;
  onError: (message: string) => void;
}) {
  const [form, setForm] = useState({
    name: trunk?.name ?? '',
    driver: trunk?.driver ?? 'sip',
    wsUri: trunk?.ws_uri ?? '',
    sipDomain: trunk?.sip_domain ?? '',
    outboundProxy: trunk?.outbound_proxy ?? '',
    authUser: trunk?.auth_user ?? '',
    authPassword: '',
    registerExpires: trunk?.register_expires ?? 600,
    stunServers: (trunk?.stun_servers ?? ['stun:stun.l.google.com:19302']).join(', '),
    turnUri: trunk?.turn_uri ?? '',
    turnUser: trunk?.turn_user ?? '',
    turnPassword: '',
    priority: trunk?.priority ?? 100,
    enabled: trunk?.enabled ?? false,
  });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const body = {
        name: form.name,
        driver: form.driver,
        wsUri: form.wsUri || null,
        sipDomain: form.sipDomain || null,
        outboundProxy: form.outboundProxy || null,
        authUser: form.authUser || null,
        // An empty password field means "leave the stored secret alone".
        ...(form.authPassword ? { authPassword: form.authPassword } : {}),
        registerExpires: Number(form.registerExpires),
        stunServers: form.stunServers.split(',').map((s) => s.trim()).filter(Boolean),
        turnUri: form.turnUri || null,
        turnUser: form.turnUser || null,
        ...(form.turnPassword ? { turnPassword: form.turnPassword } : {}),
        priority: Number(form.priority),
        enabled: form.enabled,
      };
      if (trunk) await api.patch(`/admin/sip/trunks/${trunk.id}`, body);
      else await api.post('/admin/sip/trunks', body);
      onSaved();
    } catch (err) {
      onError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={trunk ? `Edit ${trunk.name}` : 'Add a SIP trunk'}
      onClose={onClose}
      wide
      footer={
        <>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn primary" disabled={busy || !form.name} onClick={() => void submit()}>Save</button>
        </>
      }
    >
      <div className="stack">
        <div className="columns two">
          <Field label="Name">
            {(id) => <input id={id} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />}
          </Field>
          <Field label="Priority" hint="Lower wins. Handsets fail over down this list.">
            {(id) => (
              <input id={id} type="number" value={form.priority} onChange={(e) => setForm({ ...form, priority: Number(e.target.value) })} />
            )}
          </Field>
        </div>

        <Field label="WebSocket URI" hint="wss://pbx.example.org:8089/ws — browsers cannot speak SIP over UDP.">
          {(id) => (
            <input id={id} className="mono" value={form.wsUri} onChange={(e) => setForm({ ...form, wsUri: e.target.value })} />
          )}
        </Field>

        <div className="columns two">
          <Field label="SIP domain">
            {(id) => <input id={id} value={form.sipDomain} onChange={(e) => setForm({ ...form, sipDomain: e.target.value })} />}
          </Field>
          <Field label="Outbound proxy">
            {(id) => (
              <input id={id} value={form.outboundProxy} onChange={(e) => setForm({ ...form, outboundProxy: e.target.value })} />
            )}
          </Field>
        </div>

        <div className="columns two">
          <Field label="Auth user">
            {(id) => <input id={id} value={form.authUser} onChange={(e) => setForm({ ...form, authUser: e.target.value })} />}
          </Field>
          <Field
            label="Auth password"
            hint={trunk?.has_password ? 'A password is stored. Leave blank to keep it.' : 'Write-only — never returned by the API.'}
          >
            {(id) => (
              <input id={id} type="password" value={form.authPassword} onChange={(e) => setForm({ ...form, authPassword: e.target.value })} />
            )}
          </Field>
        </div>

        <Field label="STUN servers" hint="Comma separated.">
          {(id) => (
            <input id={id} className="mono" value={form.stunServers} onChange={(e) => setForm({ ...form, stunServers: e.target.value })} />
          )}
        </Field>

        <div className="columns three">
          <Field label="TURN URI" hint="Needed when agents sit behind symmetric NAT.">
            {(id) => <input id={id} value={form.turnUri} onChange={(e) => setForm({ ...form, turnUri: e.target.value })} />}
          </Field>
          <Field label="TURN user">
            {(id) => <input id={id} value={form.turnUser} onChange={(e) => setForm({ ...form, turnUser: e.target.value })} />}
          </Field>
          <Field label="TURN password">
            {(id) => (
              <input id={id} type="password" value={form.turnPassword} onChange={(e) => setForm({ ...form, turnPassword: e.target.value })} />
            )}
          </Field>
        </div>

        <label className="checkbox">
          <input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} />
          Enabled — handsets will register against this trunk
        </label>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------- taxonomy

function TaxonomyPanel({ notify }: { notify: Notify }) {
  const taxonomy = useResource<{ categories: Category[]; priorities: Priority[] }>('/admin/taxonomy');
  const [category, setCategory] = useState({ code: '', name: '', defaultPriorityId: '' });
  const [priority, setPriority] = useState({ code: '', name: '', level: 3, color: '#ca8a04', sla: '' });

  return (
    <div className="columns two">
      <Card title="Incident types" flush>
        {!taxonomy.data ? <Empty>Loading…</Empty> : (
          <>
            <table>
              <thead><tr><th>Code</th><th>Name</th><th>Default priority</th><th></th></tr></thead>
              <tbody>
                {taxonomy.data.categories.map((entry) => (
                  <tr key={entry.id}>
                    <td className="mono">{entry.code}</td>
                    <td>{entry.name}</td>
                    <td>
                      {taxonomy.data!.priorities.find((p) => p.id === entry.default_priority_id)?.code ?? '—'}
                    </td>
                    <td>
                      <ConfirmButton
                        label="Archive"
                        onConfirm={() =>
                          void run(notify, 'Type archived',
                            () => api.del(`/admin/taxonomy/categories/${entry.id}`), taxonomy.reload)
                        }
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <form
              className="stack"
              style={{ padding: 14, borderTop: '1px solid var(--border)' }}
              onSubmit={(event) => {
                event.preventDefault();
                void run(notify, 'Type saved', async () => {
                  await api.post('/admin/taxonomy/categories', {
                    code: category.code,
                    name: category.name,
                    defaultPriorityId: category.defaultPriorityId || null,
                  });
                  setCategory({ code: '', name: '', defaultPriorityId: '' });
                }, taxonomy.reload);
              }}
            >
              <div className="row">
                <input placeholder="Code" value={category.code} required
                  onChange={(e) => setCategory({ ...category, code: e.target.value.toUpperCase() })} style={{ width: 110 }} />
                <input placeholder="Name" value={category.name} required
                  onChange={(e) => setCategory({ ...category, name: e.target.value })} style={{ flex: 1 }} />
                <select value={category.defaultPriorityId}
                  onChange={(e) => setCategory({ ...category, defaultPriorityId: e.target.value })} style={{ width: 'auto' }}>
                  <option value="">Default priority…</option>
                  {taxonomy.data.priorities.map((p) => <option key={p.id} value={p.id}>{p.code}</option>)}
                </select>
                <button className="btn primary" type="submit">Save</button>
              </div>
              <span className="faint">
                Archiving keeps the label on historical incidents; it only stops the type being chosen again.
              </span>
            </form>
          </>
        )}
      </Card>

      <Card title="Priorities" flush>
        {!taxonomy.data ? <Empty>Loading…</Empty> : (
          <>
            <table>
              <thead><tr><th>Code</th><th>Name</th><th>Level</th><th>Dispatch SLA</th><th></th></tr></thead>
              <tbody>
                {taxonomy.data.priorities.map((entry) => (
                  <tr key={entry.id}>
                    <td>
                      <span className="pill-priority" style={{ background: entry.color }}>{entry.code}</span>
                    </td>
                    <td>{entry.name}</td>
                    <td className="num">{entry.level}</td>
                    <td>{entry.dispatch_sla_minutes ? `${entry.dispatch_sla_minutes} min` : <span className="faint">no clock</span>}</td>
                    <td>
                      <ConfirmButton
                        label="Archive"
                        onConfirm={() =>
                          void run(notify, 'Priority archived',
                            () => api.del(`/admin/taxonomy/priorities/${entry.id}`), taxonomy.reload)
                        }
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <form
              className="stack"
              style={{ padding: 14, borderTop: '1px solid var(--border)' }}
              onSubmit={(event) => {
                event.preventDefault();
                void run(notify, 'Priority saved', async () => {
                  await api.post('/admin/taxonomy/priorities', {
                    code: priority.code,
                    name: priority.name,
                    level: Number(priority.level),
                    color: priority.color,
                    dispatchSlaMinutes: priority.sla ? Number(priority.sla) : null,
                  });
                  setPriority({ code: '', name: '', level: 3, color: '#ca8a04', sla: '' });
                }, taxonomy.reload);
              }}
            >
              <div className="row">
                <input placeholder="Code" value={priority.code} required
                  onChange={(e) => setPriority({ ...priority, code: e.target.value.toUpperCase() })} style={{ width: 80 }} />
                <input placeholder="Name" value={priority.name} required
                  onChange={(e) => setPriority({ ...priority, name: e.target.value })} style={{ flex: 1 }} />
                <input type="number" min={1} max={99} value={priority.level} title="Level — 1 is most urgent"
                  onChange={(e) => setPriority({ ...priority, level: Number(e.target.value) })} style={{ width: 70 }} />
                <input type="color" value={priority.color}
                  onChange={(e) => setPriority({ ...priority, color: e.target.value })} style={{ width: 46, padding: 2 }} />
                <input type="number" placeholder="SLA min" value={priority.sla}
                  onChange={(e) => setPriority({ ...priority, sla: e.target.value })} style={{ width: 90 }} />
                <button className="btn primary" type="submit">Save</button>
              </div>
              <span className="faint">
                The SLA is minutes from creation to dispatch. Past it, the incident escalates on its own.
              </span>
            </form>
          </>
        )}
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------- field messages

function MessagesPanel({ notify }: { notify: Notify }) {
  const messages = useResource<{ messages: FieldMessage[]; unacknowledged: number }>('/messages?limit=100');
  const units = useResource<{ units: Unit[] }>('/units');
  const { can } = useAuth();
  const [unitId, setUnitId] = useState('');
  const [body, setBody] = useState('');

  useRealtime(['messages'], () => messages.reload(), () => messages.reload());

  return (
    <div className="columns wide-side">
      <Card
        title="Field traffic"
        flush
        actions={
          (messages.data?.unacknowledged ?? 0) > 0 ? (
            <span className="badge danger">{messages.data!.unacknowledged} unacknowledged</span>
          ) : null
        }
      >
        {!messages.data ? <Empty>Loading…</Empty> : messages.data.messages.length === 0 ? (
          <Empty>No field messages.</Empty>
        ) : (
          <ul className="list scroll-md">
            {messages.data.messages.map((message) => (
              <li key={message.id}>
                <div className="item" style={{ cursor: 'default' }}>
                  <span className="grow">
                    <span className="item-title">
                      {message.call_sign ?? 'Unknown unit'}
                      <span className={`badge ${message.direction === 'inbound' ? 'warn' : 'neutral'}`}>
                        {message.direction === 'inbound' ? 'From field' : 'To field'}
                      </span>
                      {message.incident_no && <span className="badge neutral mono">{message.incident_no}</span>}
                    </span>
                    <span className="item-sub" style={{ whiteSpace: 'pre-wrap' }}>{message.body}</span>
                    <span className="faint">
                      {message.sender_label ?? ''} · {dateTime(message.created_at)}
                      {message.acknowledged_at && ` · acknowledged by ${message.acknowledged_by_name ?? 'control'}`}
                    </span>
                  </span>
                  {message.direction === 'inbound' && !message.acknowledged_at && can('messages.send') && (
                    <button
                      className="btn sm"
                      onClick={() =>
                        void run(notify, 'Acknowledged',
                          () => api.post(`/messages/${message.id}/acknowledge`), messages.reload)
                      }
                    >
                      Acknowledge
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {can('messages.send') && (
        <Card title="Send to a unit">
          <form
            className="stack"
            onSubmit={(event) => {
              event.preventDefault();
              void run(notify, 'Message sent', async () => {
                await api.post('/messages', { unitId, body });
                setBody('');
              }, messages.reload);
            }}
          >
            <Field label="Unit">
              {(id) => (
                <select id={id} value={unitId} required onChange={(e) => setUnitId(e.target.value)}>
                  <option value="">Choose…</option>
                  {(units.data?.units ?? []).map((unit) => (
                    <option key={unit.id} value={unit.id}>{unit.call_sign} — {unit.name}</option>
                  ))}
                </select>
              )}
            </Field>
            <Field label="Message">
              {(id) => <textarea id={id} rows={4} value={body} required onChange={(e) => setBody(e.target.value)} />}
            </Field>
            <button className="btn primary" type="submit" disabled={!unitId || !body.trim()}>Send</button>
          </form>
        </Card>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- system

function SystemPanel({ notify }: { notify: Notify }) {
  const config = useResource<{
    config: Record<string, unknown>;
    shifts: Shift[];
    environment: { appEnv: string; isDemo: boolean };
  }>('/admin/config');
  const health = useResource<{ status: string; environment: string; database: string; realtimeClients: number; uptimeSeconds: number }>('/health');
  const [edits, setEdits] = useState<Record<string, string>>({});

  const save = (key: string) =>
    void run(notify, `${key} saved`, async () => {
      // Values are stored as JSON, so an operator can put a number, a flag or a
      // structure in the same box — but it has to parse before it is accepted.
      let value: unknown;
      try {
        value = JSON.parse(edits[key]);
      } catch {
        throw new Error(`${key} must be valid JSON`);
      }
      await api.put('/admin/config', { key, value });
      setEdits((current) => {
        const next = { ...current };
        delete next[key];
        return next;
      });
    }, config.reload);

  return (
    <div className="columns two">
      <Card title="Settings" flush>
        {!config.data ? <Empty>Loading…</Empty> : (
          <table>
            <thead><tr><th>Key</th><th>Value</th><th></th></tr></thead>
            <tbody>
              {Object.entries(config.data.config).map(([key, value]) => {
                const raw = edits[key] ?? JSON.stringify(value);
                return (
                  <tr key={key}>
                    <td className="mono">{key}</td>
                    <td>
                      <input
                        className="mono"
                        value={raw}
                        onChange={(event) => setEdits({ ...edits, [key]: event.target.value })}
                      />
                    </td>
                    <td>
                      <button className="btn sm" disabled={edits[key] === undefined} onClick={() => save(key)}>
                        Save
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>

      <div className="stack">
        <Card title="Instance">
          {!health.data ? <Empty>Loading…</Empty> : (
            <div className="stack tight">
              <div className="row between"><span className="faint">Environment</span><span>{health.data.environment}</span></div>
              <div className="row between">
                <span className="faint">Database</span>
                <span className={`badge ${health.data.database === 'ok' ? 'ok' : 'danger'}`}>{health.data.database}</span>
              </div>
              <div className="row between"><span className="faint">Consoles connected</span><span>{health.data.realtimeClients}</span></div>
              <div className="row between"><span className="faint">Uptime</span><span>{Math.round(health.data.uptimeSeconds / 60)} min</span></div>
            </div>
          )}
        </Card>

        <Card title="Shifts" flush>
          {!config.data ? <Empty>Loading…</Empty> : (
            <table>
              <thead><tr><th>Shift</th><th>Starts</th><th>Ends</th></tr></thead>
              <tbody>
                {config.data.shifts.map((shift) => (
                  <tr key={shift.id}>
                    <td>{shift.name}</td>
                    <td className="mono">{shift.starts_at_local}</td>
                    <td className="mono">{shift.ends_at_local}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- audit

function AuditPanel() {
  const [action, setAction] = useState('');
  const [entityType, setEntityType] = useState('');
  const query = new URLSearchParams({ limit: '200' });
  if (action) query.set('action', action);
  if (entityType) query.set('entityType', entityType);
  const audit = useResource<{ entries: AuditEntry[] }>(`/admin/audit?${query.toString()}`);

  return (
    <Card
      title="Audit trail"
      flush
      actions={
        <div className="row tight">
          <input
            placeholder="Action prefix, e.g. call."
            value={action}
            onChange={(event) => setAction(event.target.value)}
            style={{ width: 180 }}
          />
          <select value={entityType} onChange={(event) => setEntityType(event.target.value)} style={{ width: 'auto' }}>
            <option value="">All entities</option>
            {['incident', 'call', 'unit', 'user', 'sip_trunk', 'field_message', 'system_config'].map((type) => (
              <option key={type} value={type}>{titleCase(type)}</option>
            ))}
          </select>
        </div>
      }
    >
      <p className="faint" style={{ padding: '10px 14px', margin: 0 }}>
        Append-only, enforced in the database itself — these rows cannot be edited or deleted by any account,
        including this one.
      </p>
      {!audit.data ? <Empty>Loading…</Empty> : audit.data.entries.length === 0 ? (
        <Empty>No entries match.</Empty>
      ) : (
        <div className="scroll" style={{ maxHeight: 'calc(100vh - 280px)' }}>
          <table>
            <thead>
              <tr><th>When</th><th>Who</th><th>Action</th><th>Entity</th><th>Changes</th></tr>
            </thead>
            <tbody>
              {audit.data.entries.map((entry) => (
                <tr key={entry.id}>
                  <td className="faint">{dateTime(entry.created_at)}</td>
                  <td>
                    {entry.actor_label ?? 'system'}
                    <div className="faint">{entry.actor_email ?? entry.ip ?? ''}</div>
                  </td>
                  <td className="mono">{entry.action}</td>
                  <td className="faint mono truncate" style={{ maxWidth: 160 }}>
                    {entry.entity_type}
                    {entry.entity_id ? `\n${entry.entity_id.slice(0, 8)}` : ''}
                  </td>
                  <td className="mono faint truncate" style={{ maxWidth: 320 }}>
                    {Object.keys(entry.changes).length ? JSON.stringify(entry.changes) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
